import { cn } from '@/shared/lib/cn';
import { Link } from 'react-router-dom';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

export type ButtonColor =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'neutral'
  | 'navy'
  | 'cerulean'
  | 'pine'
  | 'moss'
  | 'sky';

type ButtonVariant = 'solid' | 'soft' | 'outline' | 'ghost';

type ButtonSize = 'sm' | 'md' | 'lg';

const sizeStyles: Record<ButtonSize, { base: string; icon: string }> = {
  sm: { base: 'px-3 py-1 text-xs gap-1.5', icon: 'p-1.5' },
  md: { base: 'px-4 py-2 text-sm gap-2', icon: 'p-2' },
  lg: { base: 'px-5 py-3 text-lg gap-2.5', icon: 'p-3' },
};

const solidStyles: Record<ButtonColor, string> = {
  primary: 'bg-primary text-ink-inverse hover:bg-primary-hover',
  secondary: 'bg-surface-tint text-ink hover:bg-surface-sunken',
  success: 'bg-success text-ink-inverse hover:brightness-95',
  warning: 'bg-warning text-ink hover:brightness-95',
  danger: 'bg-danger text-ink-inverse hover:brightness-95',
  info: 'bg-info text-ink-inverse hover:brightness-95',
  neutral: 'bg-neutral text-ink-inverse hover:brightness-95',
  navy: 'bg-navy text-ink-inverse hover:brightness-95',
  cerulean: 'bg-cerulean text-ink-inverse hover:brightness-95',
  pine: 'bg-pine text-ink-inverse hover:brightness-95',
  moss: 'bg-moss text-ink-inverse hover:brightness-95',
  sky: 'bg-sky text-ink-inverse hover:brightness-95',
};

const softStyles: Record<ButtonColor, string> = {
  primary: 'bg-primary-tint text-primary-ink hover:bg-primary/15',
  secondary: 'bg-surface-sunken text-ink hover:bg-surface-tint',
  success: 'bg-success-tint text-success hover:bg-success/15',
  warning: 'bg-warning-tint text-warning hover:bg-warning/15',
  danger: 'bg-danger-tint text-danger hover:bg-danger/15',
  info: 'bg-info-tint text-info hover:bg-info/15',
  neutral: 'bg-neutral-tint text-ink-muted hover:bg-surface-tint',
  navy: 'bg-navy-900 text-navy hover:bg-navy/20',
  cerulean: 'bg-cerulean-900 text-cerulean hover:bg-cerulean/20',
  pine: 'bg-pine-900 text-pine hover:bg-pine/20',
  moss: 'bg-moss-900 text-moss hover:bg-moss/20',
  sky: 'bg-sky-900 text-sky hover:bg-sky/20',
};

const outlineStyles: Record<ButtonColor, string> = {
  primary: 'border border-primary text-primary hover:bg-primary-tint',
  secondary: 'border border-ink-subtle text-ink hover:bg-surface-sunken',
  success: 'border border-success text-success hover:bg-success-tint',
  warning: 'border border-warning text-warning hover:bg-warning-tint',
  danger: 'border border-danger text-danger hover:bg-danger-tint',
  info: 'border border-info text-info hover:bg-info-tint',
  neutral: 'border border-ink-subtle text-ink-muted hover:bg-neutral-tint',
  navy: 'border border-navy text-navy hover:bg-navy-900',
  cerulean: 'border border-cerulean text-cerulean hover:bg-cerulean-900',
  pine: 'border border-pine text-pine hover:bg-pine-900',
  moss: 'border border-moss text-moss hover:bg-moss-900',
  sky: 'border border-sky text-sky hover:bg-sky-900',
};

const ghostStyles: Record<ButtonColor, string> = {
  primary: 'text-primary hover:bg-primary-tint',
  secondary: 'text-ink hover:bg-surface-sunken',
  success: 'text-success hover:bg-success-tint',
  warning: 'text-warning hover:bg-warning-tint',
  danger: 'text-danger hover:bg-danger-tint',
  info: 'text-info hover:bg-info-tint',
  neutral: 'text-ink-muted hover:bg-neutral-tint',
  navy: 'text-navy hover:bg-navy-900',
  cerulean: 'text-cerulean hover:bg-cerulean-900',
  pine: 'text-pine hover:bg-pine-900',
  moss: 'text-moss hover:bg-moss-900',
  sky: 'text-sky hover:bg-sky-900',
};

const variantStyles: Record<ButtonVariant, Record<ButtonColor, string>> = {
  solid: solidStyles,
  soft: softStyles,
  outline: outlineStyles,
  ghost: ghostStyles,
};

interface ButtonOwnProps {
  color?: ButtonColor;
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  iconOnly?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  to?: string;
  state?: unknown;
}

export type ButtonProps = ButtonOwnProps & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'>;

export const Button = ({
  className,
  color: rawColor,
  variant: rawVariant,
  size = 'md',
  fullWidth = false,
  iconOnly = false,
  leadingIcon,
  trailingIcon,
  to,
  state,
  type = 'button',
  children,
  ...rest
}: ButtonProps) => {
  const sizing = sizeStyles[size];
  const variantKey: ButtonVariant =
    rawVariant && rawVariant in variantStyles ? rawVariant : 'solid';
  const colorKey: ButtonColor =
    rawColor && rawColor in variantStyles[variantKey] ? rawColor : 'primary';

  const variantStyle = variantStyles[variantKey][colorKey];

  const classes = cn(
    'inline-flex items-center justify-center rounded-sm font-medium transition-colors cursor-pointer no-underline',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    iconOnly ? sizing.icon : sizing.base,
    fullWidth ? 'w-full' : 'w-fit',
    variantStyle,
    className,
  );

  if (to != null) {
    return (
      <Link to={to} state={state} className={classes}>
        {leadingIcon}
        {iconOnly ? null : children}
        {trailingIcon}
      </Link>
    );
  }

  return (
    <button className={classes} type={type} {...rest}>
      {leadingIcon}
      {iconOnly ? null : children}
      {trailingIcon}
    </button>
  );
};
